import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useAuth from '../../../Hooks/useAuth';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import { ACTION_POPUP_CONTENT_WIDTH_CLASS } from '../../../components/popup/popupLayoutSpec';
import { matchesUrlName } from '../../../utils/slugify';
import {
  AdminPageTitle,
  AdminPanelTitle,
  AdminHeaderAction,
  AdminFormFieldError,
  AdminDetailFieldLabel,
  AdminDetailFieldValue,
  AdminSupportingText,
  AdminFormSecondaryAction,
  AdminFormDropdownChevron,
  AdminCompactFormPage,
  adminFormDetailSelectClasses,
  adminFormDetailInputClasses,
} from '../shared/adminUx';
import {
  buildSampleTeamMembers,
  canManagePermissionsAccess,
  canViewPermissionsAccess,
  getActorDisplayName,
  TEAM_MEMBER_STATUS,
} from '../../../data/teamConfig';
import {
  accessFormFromMember,
  applyModuleActionToggle,
  DEFAULT_DATA_ACCESS_SCOPE_OPTIONS,
  emptyAccessForm,
  emptyModulePermission,
  formatCustomisedAccessDisplay,
  getDefaultDataAccessScopeLabel,
  getModuleLabel,
  MODULE_ACTION_LABELS,
  MODULE_OPTIONS,
  moduleHasAnyPermission,
  normalizeModulePermissions,
  compareModulePermissions,
  PERMISSIONS_ACCESS_ROUTE,
} from '../../../data/permissionsConfig';
import {
  ACCESS_DURATION,
  buildMaintenanceBlocks,
  buildSamplePermissionConfigs,
  findActivePermissionConfig,
  formatExtendedModuleActionsSummary,
  formatMemberDropdownLabel,
  getAssignableTeamMembers,
  getDepartmentsForTeamType,
  getJobRolesForDepartment,
  isModuleAssignableToOrdinaryMember,
  TEAM_TYPE_OPTIONS,
} from '../../../data/permissionsAccessMatrix';
import {
  ALL_ACCESS_REMOVED_SUCCESS,
  applyAccessConfigurationUpdate,
  applyInitialAccessAssignment,
  EDIT_ASSIGNMENT_SUCCESS,
  INITIAL_ASSIGNMENT_SUCCESS,
  mergeMembersAfterAccessSave,
  SAVE_FAILURE_MESSAGE,
  collectAccessSaveErrors,
} from '../../../data/permissionsWorkflow';

const MODULE_CARD_ACTION_KEYS = ['view', 'add', 'edit', 'assign', 'review'];

const TempPasswordPopup = ({ memberName, temporaryPassword, onClose }) => (
  <div
    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] px-4"
    onClick={onClose}
    role="presentation"
  >
    <div
      className={`bg-white rounded-xl shadow-lg ${ACTION_POPUP_CONTENT_WIDTH_CLASS}`}
      onClick={(e) => e.stopPropagation()}
      role="dialog"
      aria-modal="true"
    >
      <div className="relative px-5 pt-5 pb-3 border-b border-lightgray">
        <AdminPanelTitle className="text-offblack text-center">Initial Temporary Password</AdminPanelTitle>
      </div>
      <div className="px-5 py-4 space-y-3 text-center">
        <AdminSupportingText className="text-offblack">
          Initial access for {memberName} was saved successfully. Give this one-time temporary password
          to the Team Member. It expires after 24 hours if unused and is shown once.
        </AdminSupportingText>
        <p className="text-offblack text-sm font-mono break-all py-2 px-3 bg-pastalBlueWhite rounded-xl">
          {temporaryPassword}
        </p>
      </div>
      <div className="px-5 pb-5 flex justify-center">
        <AdminHeaderAction type="button" onClick={onClose}>
          Done
        </AdminHeaderAction>
      </div>
    </div>
  </div>
);

const PermissionsAccessDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { auth } = useAuth();
  const canView = canViewPermissionsAccess(auth);
  const canManage = canManagePermissionsAccess(auth);
  const actorLabel = getActorDisplayName(auth);

  const pathNorm = (location.pathname || '/').replace(/\/+$/, '') || '/';
  const isAdd = pathNorm.endsWith('/add');
  const isConfigPath = pathNorm.includes('/config/');
  const isMemberRoute =
    pathNorm.includes('/member/') ||
    (!isAdd && !isConfigPath && Boolean(params.memberName));
  const isEdit = isMemberRoute && /\/edit$/i.test(pathNorm);
  const isViewAccessDetails = isMemberRoute && !isEdit;
  const memberNameParam = params.memberName || '';

  const [configs] = useState(() => location.state?.configs || buildSamplePermissionConfigs());
  const [members, setMembers] = useState(
    () => location.state?.members || location.state?.existingMembers || buildSampleTeamMembers(),
  );
  const [formData, setFormData] = useState(() => emptyAccessForm());
  const [errors, setErrors] = useState({});
  const [saveError, setSaveError] = useState('');
  const [tempPassword, setTempPassword] = useState(null);

  const assignableMembers = useMemo(() => getAssignableTeamMembers(members), [members]);

  const nameParam = decodeURIComponent(memberNameParam || '');
  const selectedMember = useMemo(() => {
    if (isAdd) return members.find((m) => m.id === formData.memberInternalId) || null;
    if (location.state?.member && matchesUrlName(location.state.member.name, nameParam)) {
      return members.find((m) => matchesUrlName(m.name, nameParam)) || location.state.member;
    }
    return members.find((m) => matchesUrlName(m.name, nameParam)) || null;
  }, [isAdd, members, formData.memberInternalId, location.state, nameParam]);

  useEffect(() => {
    if (isAdd) return;
    if (!selectedMember) return;
    const next = accessFormFromMember(selectedMember);
    const matched = findActivePermissionConfig(configs, {
      teamType: next.teamType,
      department: next.department,
      jobRole: next.jobRole,
    });
    if (matched) {
      next.permissionConfigId = matched.id;
      next.savedPermissionConfigId = matched.id;
      next.presetModulePermissions = matched.modulePermissions || [];
    }
    next.configurationMaintenance = buildMaintenanceBlocks(
      next.modulePermissions,
      selectedMember.configurationMaintenance,
    );
    setFormData(next);
  }, [isAdd, selectedMember, configs]);

  useEffect(() => {
    if (!isAdd) return;
    setFormData(emptyAccessForm());
    setErrors({});
    setSaveError('');
  }, [isAdd]);

  const departmentOptions = useMemo(
    () => getDepartmentsForTeamType(formData.teamType),
    [formData.teamType],
  );
  const jobRoleOptions = useMemo(
    () => getJobRolesForDepartment(formData.teamType, formData.department),
    [formData.teamType, formData.department],
  );

  const eligibleModules = useMemo(
    () => MODULE_OPTIONS.filter((m) => isModuleAssignableToOrdinaryMember(m.value)),
    [],
  );

  const handleSelectTeamMember = (memberInternalId) => {
    const member = members.find((m) => m.id === memberInternalId);
    setFormData({
      ...emptyAccessForm(),
      memberInternalId: member?.id || '',
      teamMemberId: member?.teamMemberId || '',
      name: member?.name || '',
      workEmail: member?.workEmail || '',
      accountStatus: member?.status || TEAM_MEMBER_STATUS.PENDING_ACCESS,
    });
    setErrors({});
    setSaveError('');
  };

  const applyConfigToForm = (config, prev) => {
    if (!config) {
      return {
        ...prev,
        permissionConfigId: null,
        permissionPreset: '',
        defaultAccessLabel: '',
        dashboard: '',
        presetModulePermissions: [],
        modulePermissions: [],
        configurationMaintenance: [],
        hasCustomisedAccess: false,
      };
    }
    const modules = normalizeModulePermissions(config.modulePermissions);
    return {
      ...prev,
      permissionConfigId: config.id,
      permissionPreset: config.permissionPresetName,
      defaultAccessLabel: config.defaultAccessLabel,
      dashboard: config.dashboard,
      presetModulePermissions: modules,
      modulePermissions: modules,
      configurationMaintenance: buildMaintenanceBlocks(modules, prev.configurationMaintenance),
      hasCustomisedAccess: false,
    };
  };

  const handleTeamTypeChange = (teamType) => {
    setFormData((prev) =>
      applyConfigToForm(null, {
        ...prev,
        teamType,
        department: '',
        jobRole: '',
        team: '',
        position: '',
      }),
    );
  };

  const handleDepartmentChange = (department) => {
    setFormData((prev) =>
      applyConfigToForm(null, {
        ...prev,
        department,
        team: department,
        jobRole: '',
        position: '',
      }),
    );
  };

  const handleJobRoleChange = (jobRole) => {
    const matching = findActivePermissionConfig(configs, {
      teamType: formData.teamType,
      department: formData.department,
      jobRole,
    });
    setFormData((prev) =>
      applyConfigToForm(matching, {
        ...prev,
        jobRole,
        position: jobRole,
        team: prev.department,
      }),
    );
    setErrors((prev) => ({ ...prev, jobRole: undefined, modulePermissions: undefined }));
  };

  const toggleModuleAction = (moduleValue, actionKey, checked) => {
    setFormData((prev) => {
      let rows = normalizeModulePermissions(prev.modulePermissions);
      if (!rows.some((r) => r.module === moduleValue)) {
        rows = [...rows, emptyModulePermission(moduleValue)];
      }
      rows = rows.map((row) =>
        row.module === moduleValue ? applyModuleActionToggle(row, actionKey, checked) : row,
      );
      const diff = compareModulePermissions(prev.presetModulePermissions || [], rows);
      return {
        ...prev,
        modulePermissions: rows,
        hasCustomisedAccess: diff.hasCustomisedAccess,
        permissionsAdded: diff.added,
        permissionsRemoved: diff.removed,
        configurationMaintenance: buildMaintenanceBlocks(rows, prev.configurationMaintenance),
      };
    });
  };

  const setModuleScope = (moduleValue, scope) => {
    setFormData((prev) => {
      const rows = normalizeModulePermissions(prev.modulePermissions).map((row) =>
        row.module === moduleValue ? { ...row, dataAccessScope: scope } : row,
      );
      return { ...prev, modulePermissions: rows };
    });
  };

  const updateMaintenance = (moduleValue, field, value) => {
    setFormData((prev) => ({
      ...prev,
      configurationMaintenance: (prev.configurationMaintenance || []).map((b) =>
        b.module === moduleValue
          ? {
              ...b,
              [field]: value,
              ...(field === 'duration' && value === ACCESS_DURATION.ONGOING ? { expiry: '' } : {}),
            }
          : b,
      ),
    }));
    setErrors((prev) => ({ ...prev, [`maintenance_${moduleValue}`]: undefined }));
  };

  const handleSave = () => {
    if (!canManage) return;
    const member =
      selectedMember || members.find((m) => m.id === formData.memberInternalId);
    if (!member) {
      setSaveError(SAVE_FAILURE_MESSAGE);
      return;
    }
    const isInitial = isAdd || member.status === TEAM_MEMBER_STATUS.PENDING_ACCESS;
    const nextErrors = collectAccessSaveErrors(formData, { configs, isInitial });
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      setSaveError(SAVE_FAILURE_MESSAGE);
      return;
    }

    const result = isInitial
      ? applyInitialAccessAssignment(member, formData, { actorName: actorLabel })
      : applyAccessConfigurationUpdate(member, formData, { actorName: actorLabel });

    const nextMembers = mergeMembersAfterAccessSave(members, result);
    setMembers(nextMembers);

    if (result.temporaryPassword) {
      setTempPassword({
        name: result.member.name,
        password: result.temporaryPassword,
        nextMembers,
        feedback: INITIAL_ASSIGNMENT_SUCCESS,
      });
      return;
    }

    navigate(PERMISSIONS_ACCESS_ROUTE, {
      state: {
        members: nextMembers,
        configs,
        feedback: result.allAccessRemoved ? ALL_ACCESS_REMOVED_SUCCESS : EDIT_ASSIGNMENT_SUCCESS,
      },
    });
  };

  const closeTempPassword = () => {
    const payload = tempPassword;
    setTempPassword(null);
    navigate(PERMISSIONS_ACCESS_ROUTE, {
      state: {
        members: payload?.nextMembers || members,
        configs,
        feedback: payload?.feedback || INITIAL_ASSIGNMENT_SUCCESS,
      },
    });
  };

  const renderLockedField = (label, value) => (
    <div className="space-y-1 w-full min-w-0">
      <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
      <input
        type="text"
        value={value || '—'}
        readOnly
        className={adminFormDetailInputClasses(true)}
      />
    </div>
  );

  const renderDetailField = (label, value) => (
    <div className="space-y-1 w-full min-w-0">
      <AdminDetailFieldLabel>{label}</AdminDetailFieldLabel>
      <AdminDetailFieldValue className="py-2 break-words">{value || '—'}</AdminDetailFieldValue>
    </div>
  );

  const renderDisplayField = (label, value, asText) =>
    asText ? renderDetailField(label, value) : renderLockedField(label, value);

  const renderSelectField = (label, value, onChange, options, placeholder, errorKey, required = false) => (
    <div className="space-y-1 w-full min-w-0 relative">
      <AdminDetailFieldLabel>
        {label} {required ? <span className="text-alertred">*</span> : null}
      </AdminDetailFieldLabel>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        className={adminFormDetailSelectClasses(false, Boolean(errors[errorKey]))}
      >
        <option value="">{placeholder}</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
      <AdminFormDropdownChevron />
      {errors[errorKey] ? <AdminFormFieldError>{errors[errorKey]}</AdminFormFieldError> : null}
    </div>
  );

  const renderModuleAccessList = (isViewMode = false) => (
    <div
      className={
        isViewMode
          ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 w-full min-w-0 gap-3'
          : 'space-y-3 w-full min-w-0'
      }
    >
      {(isViewMode
        ? eligibleModules.filter((m) => {
            const row = normalizeModulePermissions(formData.modulePermissions).find(
              (r) => r.module === m.value,
            );
            return moduleHasAnyPermission(row);
          })
        : eligibleModules
      ).map((module) => {
        const row =
          normalizeModulePermissions(formData.modulePermissions).find(
            (r) => r.module === module.value,
          ) || emptyModulePermission(module.value);
        const hasAccess = moduleHasAnyPermission(row);
        return (
          <div
            key={module.value}
            className={`rounded-xl border p-3 flex flex-col gap-3 min-w-0 ${
              hasAccess ? 'border-primaryblue bg-pastalBlueWhite' : 'border-lightgray bg-white'
            }`}
          >
            <div className="min-w-0" title={module.label}>
              <AdminDetailFieldValue as="p" emphasis className="!mb-0 truncate text-offblack">
                {module.label}
              </AdminDetailFieldValue>
            </div>
            {isViewMode ? (
              <>
                <AdminSupportingText as="p" className="text-mediumgray">
                  {formatExtendedModuleActionsSummary(row)}
                </AdminSupportingText>
                <AdminSupportingText as="p" className="text-mediumgray">
                  {getDefaultDataAccessScopeLabel(row.dataAccessScope)}
                </AdminSupportingText>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3 flex-wrap">
                  {MODULE_CARD_ACTION_KEYS.map((actionKey) => (
                    <label
                      key={actionKey}
                      className="inline-flex items-center gap-2 cursor-pointer select-none"
                    >
                      <input
                        type="checkbox"
                        checked={Boolean(row[actionKey])}
                        onChange={(e) =>
                          toggleModuleAction(module.value, actionKey, e.target.checked)
                        }
                        className="h-4 w-4 shrink-0 rounded border-lightgray text-primaryblue focus:ring-primaryblue"
                      />
                      <AdminSupportingText as="span" className="text-offblack whitespace-nowrap">
                        {MODULE_ACTION_LABELS[actionKey]}
                      </AdminSupportingText>
                    </label>
                  ))}
                </div>
                {hasAccess ? (
                  <div className="space-y-1 w-full min-w-0 relative">
                    <AdminDetailFieldLabel>Data-access Scope</AdminDetailFieldLabel>
                    <select
                      value={row.dataAccessScope || ''}
                      onChange={(e) => setModuleScope(module.value, e.target.value)}
                      className={adminFormDetailSelectClasses(false)}
                    >
                      {DEFAULT_DATA_ACCESS_SCOPE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                    <AdminFormDropdownChevron />
                  </div>
                ) : null}
              </>
            )}
          </div>
        );
      })}
    </div>
  );

  if (!canView) {
    return (
      <AdminLayout>
        <AdminPageTitle>Permissions &amp; Access</AdminPageTitle>
        <AdminSupportingText className="text-mediumgray mt-4">
          You do not have permission to access Permissions &amp; Access.
        </AdminSupportingText>
      </AdminLayout>
    );
  }

  if (!isAdd && !selectedMember) {
    return (
      <AdminLayout>
        <AdminSupportingText className="text-mediumgray">Access not found.</AdminSupportingText>
      </AdminLayout>
    );
  }

  if ((isAdd || isEdit) && !canManage) {
    return (
      <AdminLayout>
        <AdminPageTitle>{isAdd ? 'Add Access' : 'Edit Access'}</AdminPageTitle>
        <AdminSupportingText className="text-mediumgray mt-4">
          Only the Platform Owner can create or change access.
        </AdminSupportingText>
      </AdminLayout>
    );
  }

  const pageTitle = isAdd ? 'Add Access' : isEdit ? 'Edit Access' : 'Access Detail';
  const isViewMode = isViewAccessDetails;
  const showFormBody = isAdd ? Boolean(formData.memberInternalId) : true;

  const customisedSummary = formatCustomisedAccessDisplay({
    hasCustomisedAccess: formData.hasCustomisedAccess,
    defaultAccessLabel: formData.defaultAccessLabel,
    moduleLabels: compareModulePermissions(
      formData.presetModulePermissions || [],
      formData.modulePermissions || [],
    ).changedModules,
  });

  return (
    <AdminLayout>
      <AdminCompactFormPage
        title={pageTitle}
        onSubmit={isViewMode ? undefined : handleSave}
        showSave={false}
        afterForm={
          tempPassword ? (
            <TempPasswordPopup
              memberName={tempPassword.name}
              temporaryPassword={tempPassword.password}
              onClose={closeTempPassword}
            />
          ) : null
        }
      >
        {isAdd && !isViewMode ? (
          <div className="space-y-1 w-full min-w-0 relative">
            <AdminDetailFieldLabel>
              Team Member <span className="text-alertred">*</span>
            </AdminDetailFieldLabel>
            <select
              value={formData.memberInternalId}
              onChange={(e) => handleSelectTeamMember(e.target.value)}
              className={adminFormDetailSelectClasses(false, Boolean(errors.teamMember))}
            >
              <option value="">Select Team Member</option>
              {assignableMembers.map((m) => (
                <option key={m.id} value={m.id}>
                  {formatMemberDropdownLabel(m, assignableMembers)}
                </option>
              ))}
            </select>
            <AdminFormDropdownChevron />
            {errors.teamMember ? <AdminFormFieldError>{errors.teamMember}</AdminFormFieldError> : null}
          </div>
        ) : null}

        {(isEdit || isViewMode || (isAdd && showFormBody)) && formData.teamMemberId ? (
          <>
            {renderDisplayField('Member ID', formData.teamMemberId, isViewMode)}
            {renderDisplayField('Name', formData.name, isViewMode)}
            {renderDisplayField('Work Email', formData.workEmail, isViewMode)}
            {renderDisplayField('Account status', formData.accountStatus, isViewMode)}
          </>
        ) : null}

        {showFormBody ? (
          <>
            {isViewMode ? (
              <>
                {renderDetailField('Team Type', formData.teamType || 'Not Assigned')}
                {renderDetailField('Department', formData.department || 'Not Assigned')}
                {renderDetailField('Job Role', formData.jobRole || 'Not Assigned')}
              </>
            ) : (
              <>
                {renderSelectField(
                  'Team Type',
                  formData.teamType,
                  handleTeamTypeChange,
                  TEAM_TYPE_OPTIONS,
                  'Select Team Type',
                  'teamType',
                  true,
                )}
                {renderSelectField(
                  'Department',
                  formData.department,
                  handleDepartmentChange,
                  departmentOptions,
                  'Select Department',
                  'department',
                  true,
                )}
                {renderSelectField(
                  'Job Role',
                  formData.jobRole,
                  handleJobRoleChange,
                  jobRoleOptions,
                  'Select Job Role',
                  'jobRole',
                  true,
                )}
              </>
            )}

            {renderDisplayField('Platform Role', 'Team Member', isViewMode)}
            {renderDisplayField(
              'Permission Preset',
              formData.permissionPreset || 'Not Assigned',
              isViewMode,
            )}
            {renderDisplayField(
              'Default access',
              formData.defaultAccessLabel || 'Not Assigned',
              isViewMode,
            )}
            {renderDisplayField(
              'Team dashboard',
              formData.dashboard || 'Not Assigned',
              isViewMode,
            )}
            {renderDisplayField('Customised access', customisedSummary || '—', isViewMode)}

            <div className="space-y-3 w-full min-w-0">
              <AdminPanelTitle className="text-offblack !mb-0">Module Access</AdminPanelTitle>
              {errors.modulePermissions ? (
                <AdminFormFieldError>{errors.modulePermissions}</AdminFormFieldError>
              ) : null}
              {renderModuleAccessList(isViewMode)}
            </div>

            {!isViewMode && (formData.configurationMaintenance || []).length > 0 ? (
              <div className="space-y-3 w-full min-w-0">
                <AdminPanelTitle className="text-offblack !mb-0">
                  Configuration-module maintenance
                </AdminPanelTitle>
                {(formData.configurationMaintenance || []).map((block) => (
                  <div
                    key={block.module}
                    className="rounded-xl border border-lightgray bg-white p-3 flex flex-col gap-3 min-w-0"
                  >
                    {renderLockedField('Configuration Module', getModuleLabel(block.module))}
                    {renderLockedField(
                      'Assigned Actions',
                      (block.assignedActions || []).join(', ') || '—',
                    )}
                    <div className="space-y-2">
                      <AdminDetailFieldLabel>
                        Access Duration <span className="text-alertred">*</span>
                      </AdminDetailFieldLabel>
                      <div className="flex flex-wrap gap-4">
                        {[ACCESS_DURATION.ONGOING, ACCESS_DURATION.TEMPORARY].map((opt) => (
                          <label
                            key={opt}
                            className="inline-flex items-center gap-2 cursor-pointer select-none"
                          >
                            <input
                              type="radio"
                              name={`duration-${block.module}`}
                              checked={block.duration === opt}
                              onChange={() => updateMaintenance(block.module, 'duration', opt)}
                              className="h-4 w-4 text-primaryblue focus:ring-primaryblue"
                            />
                            <AdminSupportingText as="span" className="text-offblack">
                              {opt}
                            </AdminSupportingText>
                          </label>
                        ))}
                      </div>
                    </div>
                    {block.duration === ACCESS_DURATION.TEMPORARY ? (
                      <div className="space-y-1 w-full min-w-0">
                        <AdminDetailFieldLabel>
                          Access Expiry <span className="text-alertred">*</span>
                        </AdminDetailFieldLabel>
                        <input
                          type="datetime-local"
                          value={block.expiry || ''}
                          onChange={(e) =>
                            updateMaintenance(block.module, 'expiry', e.target.value)
                          }
                          className={adminFormDetailInputClasses(
                            false,
                            Boolean(errors[`maintenance_${block.module}`]),
                          )}
                        />
                        {errors[`maintenance_${block.module}`] ? (
                          <AdminFormFieldError>
                            {errors[`maintenance_${block.module}`]}
                          </AdminFormFieldError>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}

            {!isViewMode && formData.accountStatus === TEAM_MEMBER_STATUS.PENDING_ACCESS ? (
              <AdminSupportingText className="text-mediumgray">
                An initial temporary password will be shown once after Save.
              </AdminSupportingText>
            ) : null}

            {saveError ? <AdminFormFieldError>{saveError}</AdminFormFieldError> : null}

            {!isViewMode ? (
              <div className="flex items-center justify-end gap-3 pt-2">
                <AdminFormSecondaryAction
                  type="button"
                  onClick={() =>
                    navigate(PERMISSIONS_ACCESS_ROUTE, { state: { configs, members } })
                  }
                >
                  Cancel
                </AdminFormSecondaryAction>
                <AdminHeaderAction type="button" onClick={handleSave}>
                  Save
                </AdminHeaderAction>
              </div>
            ) : null}
          </>
        ) : null}
      </AdminCompactFormPage>
    </AdminLayout>
  );
};

export default PermissionsAccessDetail;
